<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue';
import { useTieredDiscounts } from '@bc/brands';
import { useFundedDiscounts, FUNDED_DISCOUNTS_KEY, OfferBadge } from '@bc/loyalty';
import type { FundedDiscountsContext } from '@bc/loyalty';

defineOptions({
  name: 'CardProductDiscountBadges',
});

const props = defineProps<{
  brandUuid?: string;
  hideVolumeDiscount?: boolean;
}>();

const { ensureLoaded, hasVolumeBasedDiscount } = useTieredDiscounts();

// Use injected funded discounts from parent (brand.vue, product.vue, cart, catalog-hits)
// Fall back to own instance when no parent provides (e.g., recommendation widgets)
const injectedFundedDiscounts = inject<FundedDiscountsContext | undefined>(FUNDED_DISCOUNTS_KEY, undefined);
const ownFundedDiscounts = injectedFundedDiscounts ? null : useFundedDiscounts();

const hasVolumeDiscount = ref(false);

const shouldShowVolumeDiscount = computed(() => hasVolumeDiscount.value && !props.hideVolumeDiscount);

const fundedDiscountPercentage = computed(() => {
  if (!props.brandUuid) {
    return undefined;
  }
  const offer = injectedFundedDiscounts
    ? injectedFundedDiscounts.getOffer(props.brandUuid)
    : ownFundedDiscounts?.getOffer(props.brandUuid);
  return offer?.attributes.percentage;
});

onMounted(async () => {
  const promises: Promise<unknown>[] = [ensureLoaded()];

  // Only fetch funded discounts if no inject (fallback mode)
  if (!injectedFundedDiscounts && ownFundedDiscounts && props.brandUuid) {
    promises.push(ownFundedDiscounts.ensureLoaded([props.brandUuid]));
  }

  await Promise.allSettled(promises);

  if (props.brandUuid) {
    hasVolumeDiscount.value = hasVolumeBasedDiscount(props.brandUuid);
  }
});
</script>

<template>
  <div
    v-if="shouldShowVolumeDiscount || fundedDiscountPercentage"
    class="card-product-discount-badges"
  >
    <div
      v-if="shouldShowVolumeDiscount"
      class="card-product-discount-badges__volume-discount"
    >
      {{ $t('cart.tieredDiscount.badge.label') }}
    </div>
    <OfferBadge
      v-if="fundedDiscountPercentage"
      :percentage="fundedDiscountPercentage"
      class="card-product-discount-badges__funded-discount"
    />
  </div>
</template>

<style lang="scss" scoped>
.card-product-discount-badges {
  @apply ds-flex ds-flex-col ds-gap-1 ds-my-1.5;

  &__volume-discount {
    @apply ds-text-xs ds-font-bold ds-text-discount-700;
  }

  &__funded-discount {
    @apply ds-self-start;
  }
}
</style>
